import type { ProcessFieldDef } from '@/api/processStepsApi'
import type { FieldDataType } from '@/types/inventory'

/**
 * Reading and writing a process step's attribute VALUES.
 *
 * Values live in `ProcessStepConnection.attributes`, keyed by an attribute
 * definition's `key` and held as `{ active, value }` — the same shape
 * `Material.attributes` uses. Kept out of the component because two of these
 * rules are easy to get subtly wrong and neither is visible on screen when it
 * happens:
 *
 *   * a stored entry may be a BARE value rather than `{ active, value }`. The
 *     column predates the definitions, and anything an API caller wrote must
 *     still read — a chip on the operator's screen already implied "active".
 *   * dirtiness has to ignore key ORDER and ignore keys no definition covers,
 *     or Save lights up on a re-render and a stale key nobody can edit keeps it
 *     lit forever.
 */

/** The key of the seeded first attribute, whose value is membership. */
export const METHOD_ATTRIBUTE_KEY = 'method'

export interface AttributeEntry {
  active: boolean
  value: unknown
}

/** One entry, whichever shape it was stored in. */
export function readEntry(raw: unknown): AttributeEntry {
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'value' in raw) {
    const entry = raw as { active?: unknown; value: unknown }
    return { active: entry.active !== false, value: entry.value }
  }
  return { active: true, value: raw }
}

/**
 * The editable state for a step: one entry per NON-relational definition.
 *
 * The method def is skipped — its value is membership, read through the step's
 * methods endpoint — and definitions with nothing stored start from
 * `activeByDefault`, which is what that column is for.
 */
export function draftFrom(
  defs: ProcessFieldDef[],
  stored: Record<string, unknown> | undefined,
): Record<string, AttributeEntry> {
  const out: Record<string, AttributeEntry> = {}
  for (const def of defs) {
    if (def.relational) continue
    const raw = stored?.[def.key]
    out[def.key] =
      raw === undefined ? { active: def.activeByDefault, value: null } : readEntry(raw)
  }
  return out
}

/**
 * EVERY PROCESS ATTRIBUTE HAS A TYPE, and the type is one of exactly two:
 *
 *   * `attribute-flag` — the attribute records YES or NO. The switch in the
 *     authoring table IS its answer: the icon is the yes/no.
 *   * `attribute-value` — the attribute records a value: a method, a count, a
 *     number of days. It has NO flag at all, only its value.
 *
 * This is the client's model of the set, and it is the whole of what the two
 * tables in the Field Attributes panel are — the left one is the
 * attribute-flags, the right one is the attribute-values. Nothing anywhere
 * "enables" an attribute for a step: every attribute has exactly one answer,
 * and its type says what shape that answer takes.
 *
 * The type lives on the DEFINITION, as its data type: a YES/NO definition IS an
 * attribute-flag and every other data type IS an attribute-value. One fact in
 * one column, so the type and the widget can never disagree — a second stored
 * column saying "flag" next to a `number` data type would be a contradiction
 * with no answer. `attributeDataTypes` is the other direction: the data types
 * an author may pick once they have chosen the type.
 *
 * `active` in a stored entry is not the type and not a switch: it is a detail
 * of the `{ active, value }` shape `Material.attributes` shares. For an
 * attribute-flag it is always true, because a flag always has an answer; for an
 * attribute-value it says whether one was entered.
 */
export type AttributeType = 'attribute-flag' | 'attribute-value'

export function attributeType(def: Pick<ProcessFieldDef, 'dataType'>): AttributeType {
  return def.dataType === 'boolean' ? 'attribute-flag' : 'attribute-value'
}

/** What each type is called on screen — the column it fills, in one word. */
export const ATTRIBUTE_TYPE_LABELS: Record<AttributeType, string> = {
  'attribute-flag': 'Flag',
  'attribute-value': 'Value',
}

/** Both types, in the order the panel lays them out. */
export const ATTRIBUTE_TYPES: AttributeType[] = ['attribute-flag', 'attribute-value']

/**
 * The data types an attribute of this type may be given.
 *
 * An attribute-flag has exactly one: YES/NO is what "records yes or no" means,
 * so choosing the type has already chosen it, and the editor offers no second
 * pick. An attribute-value may be any of the others — and NOT boolean, which
 * would be an attribute-flag wearing the wrong type.
 */
export function attributeDataTypes(type: AttributeType, all: FieldDataType[]): FieldDataType[] {
  return type === 'attribute-flag' ? ['boolean'] : all.filter(t => t !== 'boolean')
}

/**
 * A definition's type for the editor's Type column — "Flag", or "Value ·
 * Number", because which shape of value it takes is the rest of the answer.
 */
export function describeAttributeType(
  def: Pick<ProcessFieldDef, 'dataType'>,
  dataTypeLabels: Record<FieldDataType, string>,
): string {
  const type = attributeType(def)
  const label = ATTRIBUTE_TYPE_LABELS[type]
  return type === 'attribute-flag' ? label : `${label} · ${dataTypeLabels[def.dataType]}`
}

/**
 * The yes/no an attribute-flag holds.
 *
 * Reads `value`, which is where the answer lives. Rows written before the
 * answer lived there carried it in `active` instead — a flag switched on with
 * nothing in `value` meant yes — so those still read correctly rather than
 * needing a migration.
 */
export function flagAnswer(entry: AttributeEntry | undefined): boolean {
  if (!entry) return false
  if (entry.value === null || entry.value === undefined) return entry.active
  return Boolean(entry.value)
}

/**
 * The entry an attribute-flag holds for `answer`.
 *
 * `active` is true either way: NO is an answer the step records, not the step
 * leaving the attribute out. Storing it as anything else is what made a step
 * read "No attributes configured" while its authoring table showed answers.
 */
export function entryForFlag(answer: boolean): AttributeEntry {
  return { active: true, value: answer }
}

/**
 * The entry an attribute-value holds for `value`.
 *
 * With no flag beside it, the box IS the answer: something entered means the
 * step records it, an empty box means there is nothing to record. A blank is
 * normalised to `null` so a cleared box and a never-filled one are the same
 * stored entry, and `attributesDirty` does not read `''` and `null` as a
 * change.
 */
export function entryForValue(value: unknown): AttributeEntry {
  return isBlankValue(value) ? { active: false, value: null } : { active: true, value }
}

/** Blank for this purpose: an empty enum pick list counts, `false` does not. */
function isBlankValue(value: unknown): boolean {
  if (Array.isArray(value)) return value.length === 0
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
}

/** What to send: every entry, keyed as stored. */
export function payloadFrom(draft: Record<string, AttributeEntry>): Record<string, AttributeEntry> {
  return { ...draft }
}

/** Has anything the definitions cover actually changed? */
export function attributesDirty(
  defs: ProcessFieldDef[],
  stored: Record<string, unknown> | undefined,
  draft: Record<string, AttributeEntry>,
): boolean {
  for (const def of defs) {
    if (def.relational) continue
    const before = draftFrom([def], stored)[def.key]
    const after = draft[def.key]
    if (!after) continue
    if (before.active !== after.active) return true
    // JSON-compared so an enum's array of picks is handled without a second
    // comparison path.
    if (JSON.stringify(before.value ?? null) !== JSON.stringify(after.value ?? null)) return true
  }
  return false
}

/** Order is not meaning: the method membership is a set. */
export function sameMethodSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const inB = new Set(b)
  return a.every((id) => inB.has(id))
}

/**
 * What one attribute's value reads as on the operator's screen.
 *
 * Its own function, and in this module rather than in the renderer, because the
 * component file is `.tsx` and vitest here collects `.test.ts` only — a rule
 * living in the component is a rule with no test.
 *
 * VALUE attributes only — a flag's yes/no comes from `flagAnswer`, so this has
 * no boolean branch and cannot disagree with it.
 *
 * An ACTIVE attribute with nothing in it still prints — as an em dash. The
 * operator is being told the step has a priority and nobody set it, which is a
 * different and more useful statement than the step having no priority at all.
 */
export function displayAttributeValue(value: unknown): string {
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '—'
  if (value === null || value === undefined || value === '') return '—'
  return String(value)
}

/**
 * What ONE attribute reads as on the operator's screen, or `null` when the step
 * does not record it.
 *
 * Takes the RAW stored entry, so the two kinds are decided in one place rather
 * than in the renderer: a flag prints its answer either way — No is an answer —
 * and a value prints only when one was entered.
 */
export function readAttributeRow(
  def: Pick<ProcessFieldDef, 'dataType'>,
  raw: unknown,
): string | null {
  // A key with nothing stored is not an attribute the step records — not a
  // flag answered No.
  if (raw === undefined) return null
  const entry = readEntry(raw)
  if (attributeType(def) === 'attribute-flag') return flagAnswer(entry) ? 'Yes' : 'No'
  if (!entry.active) return null
  return displayAttributeValue(entry.value)
}

/**
 * The Process Output attribute's key (z8nrz7f1dz): KIT makes the step's form
 * build a kit when it is completed; unset reads as Process, the default.
 */
export const PROCESS_OUTPUT_KEY = 'processOutput'
